<template>
    <div class="prepare-wrap" :style="reportContainerTopMargin">
        <div class="top-text-wrap">
            <p class="h5">첫 리포트 발급 예정</p>
            <p class="h5--regular">{{ computedFirstReportDate }}</p>
        </div>

        <div class="spinner-container">
            <p class="h3" v-dompurify-html="props.data?.DutyExplanationTitle ?? ''"></p>
            <p class="spinner-desc-text caption--regular" v-dompurify-html="computedProgressDescription"></p>
            <div class="spinner-wrap" ref="spinnerWrap">
                <!-- 원형 로딩 스피너 -->
                <StatusRing
                    :percentage="props.data?.CurrentMeasureCountPercentage ?? 0"
                    :size="ringSize"
                    :strokeWidth="6"
                />
            </div>
            <p
                class="progress-status-text body-3--regular"
                v-dompurify-html="props.data?.DutyExplanationContent ?? ''"
            ></p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, onUnmounted } from 'vue';
import { StatusRing } from '@common/trend-report-common';
import { sendHeaderHiddenStatusToNative } from '@common/common/utils/utils';
import type { ReportCompletionGageResDto } from '@common/openapi-client/openapi';

const PROGRESS_DESCRIPTION = '리포트 발급을 위한 검사 <b>#ToTalDutyMeasureCount#회 중</b> #CurrentMeasureCount#했어요.';

const spinnerWrap = ref<HTMLDivElement>();
const ringSize = ref(228);

const props = defineProps<{
    data: ReportCompletionGageResDto | null;
}>();

const computedProgressDescription = computed(() => {
    return PROGRESS_DESCRIPTION.replace(
        '#ToTalDutyMeasureCount#',
        String(props.data?.ToTalDutyMeasureCount ?? ''),
    ).replace('#CurrentMeasureCount#', `<b class="highlight-text">${props.data?.CurrentMeasureCount}회를 완료</b>`);
});

const computedFirstReportDate = computed(() => {
    const date = new Date(props.data?.UpcomingReportDueDateTime ?? '');

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekday = date.toLocaleDateString('ko-KR', {
        weekday: 'short',
        timeZone: 'Asia/Seoul',
    });

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const isAM = hours < 12;
    const period = isAM ? '오전' : '오후';
    if (!isAM && hours > 12) {
        hours -= 12;
    }
    if (hours === 0) {
        hours = 12; // 오전 0시를 오전 12시로 표기
    }

    return `${year}년 ${month}월 ${day}일(${weekday}) ${period} ${hours}:${minutes}`;
});

const HEADER_HEIGHT = 115;

const reportContainerTopMargin = computed(() => {
    // 네이티브 상단 메뉴바 높이 맞추기 위해 사용

    return {
        marginTop: `${HEADER_HEIGHT}px`,
    };
});

let lastScrollTop = ref(0);
let isHideHeader = ref(true); // 현재 상태 기억

const handleScroll = () => {
    const appEl = document.querySelector('#app');

    if (!appEl) return;

    const currentScrollTop = appEl.scrollTop;

    // 1. 맨 위에 닿았을 때
    if (currentScrollTop <= 0) {
        if (!isHideHeader.value) {
            sendHeaderHiddenStatusToNative(false);
            isHideHeader.value = true;
        }
    }
    // 2. 스크롤을 아래로 내릴 때 (그리고 기존 상태가 true였을 때만)
    else if (currentScrollTop > lastScrollTop.value) {
        if (isHideHeader.value) {
            sendHeaderHiddenStatusToNative(true);
            isHideHeader.value = false;
        }
    }

    lastScrollTop.value = currentScrollTop <= 0 ? 0 : currentScrollTop;
};

onMounted(async () => {
    await nextTick();
    if (spinnerWrap.value) {
        ringSize.value = spinnerWrap.value.clientWidth;
    }

    const appEl = document.querySelector('#app');
    appEl?.addEventListener('scroll', handleScroll, { passive: true });
});

onUnmounted(() => {
    const appEl = document.querySelector('#app');
    appEl?.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped lang="scss">
.prepare-wrap {
    padding: 4rem 1.5rem 0;
}

.top-text-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 2rem;
    color: $grey-8;
}

.spinner-container {
    max-width: 36rem;
    margin: 0 auto;
    background: $grey-8;
    color: $primary-white;
    padding: 3rem 1.5rem;
    border-radius: 3rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-shadow: 0px 30px 30px 0px #0000001a;
    margin-bottom: 4rem;

    .spinner-wrap {
        width: 70%;
        aspect-ratio: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 3rem 0;
    }

    .spinner-desc-text {
        text-align: center;
        white-space: pre-line;
        padding-top: 3rem;
    }
}

:deep(.progress-status-text) {
    width: 80%;
    text-align: center;
    color: $primary-white;

    b {
        color: $pro-secondary-blue;
    }
}

:deep(.highlight-text) {
    color: $pro-secondary-blue;
}
</style>
